import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast } from "sonner";

export default function CheckoutPage() {
  const cart = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shipDifferent, setShipDifferent] = useState(false);
  const [minOrder, setMinOrder] = useState(0);
  const [shipping, setShipping] = useState({
    shipping_recipient: user?.company_name || "",
    shipping_address: "",
    shipping_postal_code: "",
    shipping_city: "Genève",
  });
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => setMinOrder(data.min_order_amount || 0)).catch(() => {});
  }, []);

  const belowMin = minOrder > 0 && cart.subtotal < minOrder;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (cart.items.length === 0) {
      setError("Votre panier est vide.");
      return;
    }
    if (belowMin) {
      setError(`Le montant minimum de commande est de CHF ${Number(minOrder).toFixed(2)}.`);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: cart.items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          unit: i.unit,
          price: i.price,
          quantity: Number(i.quantity),
        })),
        notes,
        shipping_same_as_billing: !shipDifferent,
        requested_delivery_date: date,
        ...(shipDifferent ? shipping : {}),
      };
      const { data } = await api.post("/orders", payload);
      cart.clear();
      toast.success("Commande envoyée !");
      navigate(`/orders/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div data-testid="checkout-page">
      <Header />
      <section className="ff-container py-12">
        <p className="ff-overline">Validation</p>
        <h1 className="font-serif text-4xl lg:text-5xl mt-3">Finaliser ma commande</h1>

        <form onSubmit={submit} className="mt-10 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-6">
            {/* Billing info */}
            <div className="ff-card p-6">
              <p className="ff-overline">Adresse de facturation</p>
              <div className="mt-4 text-sm text-[#1A2B1E] leading-relaxed">
                <p className="font-medium" data-testid="checkout-company">{user.company_name}</p>
                <p>{user.first_name} {user.last_name}</p>
                <p>N° entreprise : {user.company_number}</p>
                <p>{user.address}</p>
                <p>{user.postal_code} {user.city}</p>
                <p>Tél : {user.phone}</p>
              </div>
            </div>

            {/* Shipping */}
            <div className="ff-card p-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shipDifferent}
                  onChange={(e) => setShipDifferent(e.target.checked)}
                  data-testid="checkout-different-shipping-checkbox"
                  className="w-4 h-4"
                />
                <span className="font-medium">Livrer à une autre adresse</span>
              </label>
              {shipDifferent && (
                <div className="mt-5 grid grid-cols-2 gap-4">
                  <Field className="col-span-2" label="Destinataire / Lieu" value={shipping.shipping_recipient} onChange={(v) => setShipping((s) => ({ ...s, shipping_recipient: v }))} testid="shipping-recipient" />
                  <Field className="col-span-2" label="Adresse" value={shipping.shipping_address} onChange={(v) => setShipping((s) => ({ ...s, shipping_address: v }))} testid="shipping-address" />
                  <Field label="Code postal" value={shipping.shipping_postal_code} onChange={(v) => setShipping((s) => ({ ...s, shipping_postal_code: v }))} testid="shipping-postal" />
                  <Field label="Ville" value={shipping.shipping_city} onChange={(v) => setShipping((s) => ({ ...s, shipping_city: v }))} testid="shipping-city" />
                </div>
              )}
            </div>

            <div className="ff-card p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#4A5D4E]">Date de livraison souhaitée</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="ff-input mt-2" data-testid="checkout-date-input" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#4A5D4E]">Notes (optionnel)</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="ff-input mt-2" placeholder="Précisions, créneau de livraison..." data-testid="checkout-notes-input" />
              </div>
            </div>
          </div>

          <aside className="ff-card p-6 h-fit lg:sticky lg:top-28">
            <p className="ff-overline">Résumé</p>
            <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
              {cart.items.map((i) => (
                <div key={i.product_id} className="flex justify-between text-sm">
                  <span className="text-[#4A5D4E]">{i.name} × {i.quantity}{i.unit}</span>
                  <span>CHF {(i.price * Number(i.quantity || 0)).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-5 border-t border-[#E5E3DC] space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-[#4A5D4E]">Sous-total</span><span data-testid="checkout-subtotal">CHF {cart.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-xs text-[#8A9A8E]"><span>dont TVA 8.1% incluse</span><span>CHF {(cart.subtotal * 0.081 / 1.081).toFixed(2)}</span></div>
            </div>
            <div className="mt-3 pt-3 border-t border-[#E5E3DC] flex justify-between text-lg font-medium">
              <span>Total TTC</span>
              <span className="font-serif text-2xl text-[#2D6A4F]" data-testid="checkout-total">
                CHF {cart.subtotal.toFixed(2)}
              </span>
            </div>
            {error && <div className="mt-3 text-sm text-[#D62828]" data-testid="checkout-error">{error}</div>}
            {minOrder > 0 && (
              <div
                className={`mt-3 text-xs ${belowMin ? "text-[#D62828]" : "text-[#8A9A8E]"}`}
                data-testid="checkout-min-order-notice"
              >
                Commande minimum : CHF {Number(minOrder).toFixed(2)} HT
                {belowMin && ` · il manque CHF ${(minOrder - cart.subtotal).toFixed(2)}`}
              </div>
            )}
            <button
              type="submit"
              disabled={loading || belowMin}
              className="ff-btn-primary w-full justify-center mt-6"
              data-testid="checkout-submit-button"
            >
              {belowMin ? "Montant minimum non atteint" : loading ? "Envoi..." : "Confirmer la commande"}
            </button>
            <p className="text-xs text-[#8A9A8E] text-center mt-3">
              Paiement sur facture · Aucune carte requise
            </p>
          </aside>
        </form>
      </section>
      <Footer />
    </div>
  );
}

function Field({ label, value, onChange, testid, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs font-semibold uppercase tracking-wider text-[#4A5D4E]">{label}</label>
      <input className="ff-input mt-2" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

